import { useState } from 'react';
import { subMonths, endOfDay, startOfDay } from 'date-fns';
import {
  AreaChart, Area, BarChart, Bar, PieChart, Pie, Cell,
  LineChart, Line,
  XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend,
} from 'recharts';
import { Loader2 } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { trpc } from '@/lib/trpc';
import { formatCurrency, formatNumber } from '@/lib/utils';
import { DateRangePicker } from '@/components/shared/date-range-picker';
import { StatCard } from '@/components/shared/stat-card';

const COLORS = [
  'hsl(152, 69%, 40%)',
  'hsl(221, 83%, 53%)',
  'hsl(38, 92%, 50%)',
  'hsl(0, 72%, 51%)',
  'hsl(280, 68%, 50%)',
  'hsl(180, 60%, 45%)',
  'hsl(330, 65%, 50%)',
  'hsl(200, 70%, 50%)',
  'hsl(60, 70%, 45%)',
];

const tooltipStyle = {
  backgroundColor: 'hsl(var(--card))',
  border: '1px solid hsl(var(--border))',
  borderRadius: '8px',
};

const axisProps = {
  stroke: 'hsl(var(--muted-foreground))',
  fontSize: 12,
  tickLine: false as const,
  axisLine: false as const,
};

interface TradingCustomerAnalyticsTabProps {
  customerId: string;
}

export function TradingCustomerAnalyticsTab({ customerId }: TradingCustomerAnalyticsTabProps) {
  const { t } = useTranslation('trading');
  const [dateRange, setDateRange] = useState({
    from: startOfDay(subMonths(new Date(), 12)),
    to: endOfDay(new Date()),
  });

  const queryInput = { customerId, startDate: dateRange.from, endDate: dateRange.to };

  const { data: stats, isLoading: statsLoading } =
    trpc.tradingCustomerAnalytics.stats.useQuery(queryInput);

  const { data: revenueOverTime, isLoading: revenueLoading } =
    trpc.tradingCustomerAnalytics.revenueOverTime.useQuery(queryInput);

  const { data: ordersByStatus } =
    trpc.tradingCustomerAnalytics.ordersByStatus.useQuery(queryInput);

  const { data: topProducts } =
    trpc.tradingCustomerAnalytics.topProducts.useQuery(queryInput);

  const { data: paymentTimeline } =
    trpc.tradingCustomerAnalytics.paymentTimeline.useQuery(queryInput);

  const { data: quoteConversion } =
    trpc.tradingCustomerAnalytics.quoteConversion.useQuery(queryInput);

  const { data: invoiceAging } =
    trpc.tradingCustomerAnalytics.invoiceAging.useQuery({ customerId });

  const { data: productBreakdown } =
    trpc.tradingCustomerAnalytics.productBreakdown.useQuery(queryInput);

  if (statsLoading || revenueLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
        <span className="ml-2 text-muted-foreground">{t('customers.analytics.loading')}</span>
      </div>
    );
  }

  const agingData = invoiceAging
    ? [
        { bucket: 'Current', amount: invoiceAging.current, color: COLORS[0] },
        { bucket: '1-30 days', amount: invoiceAging.days30, color: COLORS[1] },
        { bucket: '31-60 days', amount: invoiceAging.days60, color: COLORS[2] },
        { bucket: '61-90 days', amount: invoiceAging.days90, color: COLORS[3] },
        { bucket: '90+ days', amount: invoiceAging.over90, color: COLORS[4] },
      ].filter((d) => d.amount > 0)
    : [];

  return (
    <div className="space-y-6">
      {/* Date Range Picker */}
      <div className="flex justify-end">
        <DateRangePicker value={dateRange} onChange={setDateRange} />
      </div>

      {/* Stat Cards */}
      <div className="grid grid-cols-2 gap-4 md:grid-cols-3 lg:grid-cols-6">
        {/* Revenue/profit/avg-order are TRY: the server converts each order at
            its own order-date rate (see orderCurrency.ts) — raw sums across
            EUR/USD/GBP/TRY orders were meaningless. */}
        <StatCard
          title="Revenue"
          value={stats?.revenue.value ?? 0}
          format="currency"
          currency="TRY"
          change={stats?.revenue.change}
        />
        <StatCard
          title="Gross Profit"
          value={stats?.grossProfit.value ?? 0}
          format="currency"
          currency="TRY"
          change={stats?.grossProfit.change}
        />
        <StatCard
          title="Margin %"
          value={stats?.profitMargin.value ?? 0}
          format="percent"
          change={stats?.profitMargin.change}
        />
        <StatCard
          title="Orders"
          value={stats?.orders.value ?? 0}
          format="number"
          change={stats?.orders.change}
        />
        <StatCard
          title="Avg Order"
          value={stats?.avgOrderValue.value ?? 0}
          format="currency"
          currency="TRY"
          change={stats?.avgOrderValue.change}
        />
        <StatCard
          title="Outstanding"
          value={stats?.outstandingBalance ?? 0}
          format="currency"
          currency="TRY"
        />
      </div>

      {/* Revenue & Profit + Orders Over Time */}
      <div className="grid gap-4 md:grid-cols-2">
        <div className="border border-border bg-card p-6">
          <h3 className="mb-4 font-semibold">{t('customers.analytics.revenueAndProfit')}</h3>
          <ResponsiveContainer width="100%" height={250}>
            <AreaChart data={revenueOverTime ?? []}>
              <defs>
                <linearGradient id="tradingRevenueGradient" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="hsl(152, 69%, 40%)" stopOpacity={0.3} />
                  <stop offset="95%" stopColor="hsl(152, 69%, 40%)" stopOpacity={0} />
                </linearGradient>
                <linearGradient id="tradingProfitGradient" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="hsl(221, 83%, 53%)" stopOpacity={0.3} />
                  <stop offset="95%" stopColor="hsl(221, 83%, 53%)" stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
              <XAxis dataKey="month" {...axisProps} />
              <YAxis {...axisProps} tickFormatter={(v) => `₺${(v / 1000).toFixed(0)}k`} />
              <Tooltip
                contentStyle={tooltipStyle}
                formatter={(value: number, name: string) => [formatCurrency(value, 'TRY'), name]}
              />
              <Legend />
              <Area
                type="monotone"
                dataKey="revenue"
                name="Revenue"
                stroke="hsl(152, 69%, 40%)"
                fill="url(#tradingRevenueGradient)"
                strokeWidth={2}
              />
              <Area
                type="monotone"
                dataKey="profit"
                name="Profit"
                stroke="hsl(221, 83%, 53%)"
                fill="url(#tradingProfitGradient)"
                strokeWidth={2}
              />
            </AreaChart>
          </ResponsiveContainer>
        </div>

        <div className="border border-border bg-card p-6">
          <h3 className="mb-4 font-semibold">{t('customers.analytics.ordersOverTime')}</h3>
          <ResponsiveContainer width="100%" height={250}>
            <BarChart data={revenueOverTime ?? []}>
              <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
              <XAxis dataKey="month" {...axisProps} />
              <YAxis {...axisProps} />
              <Tooltip
                contentStyle={tooltipStyle}
                formatter={(value: number) => [formatNumber(value), 'Orders']}
              />
              <Bar dataKey="orders" fill={COLORS[1]} radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Order Status Donut + Top Products */}
      <div className="grid gap-4 md:grid-cols-2">
        <div className="border border-border bg-card p-6">
          <h3 className="mb-4 font-semibold">{t('customers.analytics.ordersByStatus')}</h3>
          <ResponsiveContainer width="100%" height={250}>
            <PieChart>
              <Pie
                data={ordersByStatus ?? []}
                cx="50%"
                cy="50%"
                innerRadius={60}
                outerRadius={90}
                paddingAngle={3}
                dataKey="count"
                nameKey="status"
              >
                {(ordersByStatus ?? []).map((_, index) => (
                  <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                ))}
              </Pie>
              <Tooltip
                contentStyle={tooltipStyle}
                formatter={(value: number, name: string) => [formatNumber(value), name]}
              />
              <Legend />
            </PieChart>
          </ResponsiveContainer>
        </div>

        <div className="border border-border bg-card p-6">
          <h3 className="mb-4 font-semibold">{t('customers.analytics.topProducts')}</h3>
          <ResponsiveContainer width="100%" height={250}>
            <BarChart data={topProducts ?? []} layout="vertical">
              <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
              <XAxis type="number" {...axisProps} tickFormatter={(v) => `₺${(v / 1000).toFixed(0)}k`} />
              <YAxis
                type="category"
                dataKey="name"
                {...axisProps}
                width={120}
                tick={{ fontSize: 11 }}
              />
              <Tooltip
                contentStyle={tooltipStyle}
                formatter={(value: number) => [formatCurrency(value, 'TRY'), 'Revenue']}
              />
              <Bar dataKey="revenue" fill={COLORS[0]} radius={[0, 4, 4, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Payment Timeline + Quote Conversion */}
      <div className="grid gap-4 md:grid-cols-2">
        <div className="border border-border bg-card p-6">
          <h3 className="mb-4 font-semibold">{t('customers.analytics.paymentTimeline')}</h3>
          <ResponsiveContainer width="100%" height={250}>
            <BarChart data={paymentTimeline ?? []}>
              <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
              <XAxis dataKey="month" {...axisProps} />
              {/* Server sums payments in TRY at payment-date rates */}
              <YAxis {...axisProps} tickFormatter={(v) => `₺${(v / 1000).toFixed(0)}k`} />
              <Tooltip
                contentStyle={tooltipStyle}
                formatter={(value: number) => [formatCurrency(value, 'TRY'), 'Payments']}
              />
              <Bar dataKey="amount" name="Payments" fill={COLORS[0]} radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>

        <div className="border border-border bg-card p-6">
          <h3 className="mb-4 font-semibold">{t('customers.analytics.quoteConversion')}</h3>
          <ResponsiveContainer width="100%" height={250}>
            <LineChart data={quoteConversion ?? []}>
              <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
              <XAxis dataKey="month" {...axisProps} />
              <YAxis yAxisId="left" {...axisProps} />
              <YAxis
                yAxisId="right"
                orientation="right"
                {...axisProps}
                tickFormatter={(v) => `${v}%`}
              />
              <Tooltip
                contentStyle={tooltipStyle}
                formatter={(value: number, name: string) =>
                  name === 'Conversion Rate'
                    ? [`${value.toFixed(1)}%`, name]
                    : [formatNumber(value), name]
                }
              />
              <Legend />
              <Line
                yAxisId="left"
                type="monotone"
                dataKey="quotes"
                name="Quotes"
                stroke={COLORS[1]}
                strokeWidth={2}
                dot={false}
              />
              <Line
                yAxisId="right"
                type="monotone"
                dataKey="rate"
                name="Conversion Rate"
                stroke={COLORS[0]}
                strokeWidth={2}
                dot={false}
              />
            </LineChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Invoice Aging */}
      <div className="border border-border bg-card p-6">
        <h3 className="mb-4 font-semibold">{t('customers.analytics.invoiceAging')}</h3>
        {agingData.length > 0 ? (
          <ResponsiveContainer width="100%" height={200}>
            <BarChart data={agingData} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
              <XAxis dataKey="bucket" {...axisProps} />
              {/* Server expresses aging buckets in TRY at the latest rate */}
              <YAxis {...axisProps} tickFormatter={(v) => `₺${(v / 1000).toFixed(0)}k`} />
              <Tooltip
                contentStyle={tooltipStyle}
                formatter={(value: number) => [formatCurrency(value, 'TRY'), 'Outstanding']}
              />
              <Bar dataKey="amount" radius={[4, 4, 0, 0]}>
                {agingData.map((entry, index) => (
                  <Cell key={`aging-${index}`} fill={entry.color} />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        ) : (
          <p className="text-sm text-muted-foreground">{t('customers.analytics.noOutstandingInvoices')}</p>
        )}
      </div>

      {/* Product Breakdown Table */}
      <div className="border border-border bg-card p-6">
        <h3 className="mb-4 font-semibold">{t('customers.analytics.productPurchaseBreakdown')}</h3>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-border text-left">
                <th className="p-2 font-medium text-muted-foreground">{t('customers.analytics.product')}</th>
                <th className="p-2 font-medium text-muted-foreground">{t('customers.analytics.brand')}</th>
                <th className="p-2 font-medium text-muted-foreground text-right">{t('customers.analytics.qty')}</th>
                <th className="p-2 font-medium text-muted-foreground text-right">{t('customers.analytics.revenue')}</th>
                <th className="p-2 font-medium text-muted-foreground text-right">{t('customers.analytics.profit')}</th>
                <th className="p-2 font-medium text-muted-foreground text-right">{t('customers.analytics.orders')}</th>
              </tr>
            </thead>
            <tbody>
              {(productBreakdown ?? []).map((item, index) => (
                <tr key={index} className="border-b border-border last:border-0">
                  <td className="p-2">{item.name}</td>
                  <td className="p-2 text-muted-foreground">{item.brand}</td>
                  <td className="p-2 text-right">{formatNumber(item.quantity)}</td>
                  <td className="p-2 text-right">{formatCurrency(item.revenue, 'TRY')}</td>
                  <td className="p-2 text-right">{formatCurrency(item.profit, 'TRY')}</td>
                  <td className="p-2 text-right">{formatNumber(item.orders)}</td>
                </tr>
              ))}
              {(!productBreakdown || productBreakdown.length === 0) && (
                <tr>
                  <td colSpan={6} className="p-4 text-center text-muted-foreground">
                    No product data available for this period.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
